// src/components/ui/OverlayListModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import Icon from "../AppIcon";

/**
 * Modal simplu (overlay) cu listă + căutare.
 *
 * items: [{ id, title, subtitle, meta, right }]
 * - title/subtitle/meta/right sunt strings (opțional)
 * - onItemClick(item) -> opțional
 */
export default function OverlayListModal({
  isOpen,
  title = "Listă",
  items = [],
  onClose,
  onItemClick,
  searchable = true,
  emptyText = "Nu există elemente.",
}) {
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setQ("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const filtered = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const term = (q || "").trim().toLowerCase();
    if (!term) return list;
    return list.filter((it) => {
      const hay = `${it?.title || ""} ${it?.subtitle || ""} ${it?.meta || ""} ${it?.right || ""}`
        .toLowerCase()
        .trim();
      return hay.includes(term);
    });
  }, [items, q]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[350] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={() => onClose?.()}
      onTouchStart={() => onClose?.()}
    >
      <div
        className="w-full sm:max-w-2xl bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[90dvh]"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-base sm:text-lg font-semibold text-foreground">{title}</div>
            <div className="text-xs text-muted-foreground">
              {filtered.length} / {(Array.isArray(items) ? items.length : 0)}
            </div>
          </div>
          <button
            type="button"
            className="p-2 rounded-md hover:bg-muted"
            onClick={() => onClose?.()}
            aria-label="Închide"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Search */}
        {searchable ? (
          <div className="px-4 sm:px-6 pt-4">
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Caută..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
        ) : null}

        {/* List */}
        <div className="px-4 sm:px-6 py-4 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
              {filtered.map((it, idx) => {
                const clickable = typeof onItemClick === "function";
                return (
                  <button
                    key={it?.id ?? `${it?.title || "item"}-${idx}`}
                    type="button"
                    onClick={() => (clickable ? onItemClick(it) : undefined)}
                    className={`w-full text-left px-4 py-3 bg-card hover:bg-muted/50 transition-colors flex items-start justify-between gap-4 ${
                      clickable ? "cursor-pointer" : "cursor-default"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-foreground truncate">{it?.title || "—"}</div>
                      {it?.subtitle ? (
                        <div className="text-sm text-muted-foreground truncate">{it.subtitle}</div>
                      ) : null}
                      {it?.meta ? (
                        <div className="text-xs text-muted-foreground mt-1 truncate">{it.meta}</div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {it?.right ? (
                        <div className="text-sm text-foreground/80">{it.right}</div>
                      ) : null}
                      {clickable ? <Icon name="ChevronRight" size={18} className="text-muted-foreground" /> : null}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
